import { generatePath } from 'react-router-dom'
import { mdiAlert, mdiCloudDownloadOutline } from '@mdi/js'
import { FormattedMessage } from 'react-intl'

import { useAppSelector } from 'app/hooks'
import IntlFormattedDate from 'app/i18n/IntlFormattedDate'
import ROUTES from 'app/routes'
import { makePublicUrl } from 'app/utils/publicUrl'
import { CommonLink } from 'components/Button'
import { PaymentSystem } from 'components/Card/styles'
import Tooltip from 'components/Tooltip'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import {
  BillHistoryAmountCell,
  BillHistoryDateCell,
  BillHistoryDescriptionCell,
  BillHistoryDownloadIcon,
  BillHistoryErrorIcon,
  BillHistoryHomeNameCell,
  BillHistoryHomeNameLink,
  BillHistoryMobileRow,
  BillHistoryMobileWhiteBlock,
  BillHistoryPaymentCell,
  BillHistoryStatusCell,
} from '../BillingHistoryTable/styles'
import type { BillingChargeType } from '../types'
import { isChargeSuccess } from '../utils'
import { commonMessages } from 'app/i18n/messages'

interface BillingHistoryMobileProps {
  original: BillingChargeType
  values: Record<string, any>
}

export function BillingHistoryMobile(props: BillingHistoryMobileProps) {
  const { original } = props

  const currentRootId = useAppSelector(currentRootDomainIdSelector)

  const homeStatusPath = generatePath(ROUTES.homeStatus, {
    rootDomainId: currentRootId || '',
    homeId: original.homeId || '  ',
  })

  const FormatDescription = (original: BillingChargeType) => {
    let decrName = commonMessages.essentialPlan
    switch (original.desc) {
      case 'NAI - Access Points':
        decrName = commonMessages.networkPlan
        break
      default:
        decrName = commonMessages.essentialPlan
    }
    return decrName
  }

  return (
    <BillHistoryMobileWhiteBlock>
      <BillHistoryMobileRow>
        <BillHistoryDateCell $failed={!isChargeSuccess(original.status)}>
          <IntlFormattedDate date={original.date * 1000} withTime />
        </BillHistoryDateCell>

        {false ?? (
          <BillHistoryStatusCell>
            {isChargeSuccess(original.status) ? (
              <CommonLink
                to={{ pathname: original.receipt_url }}
                target="_blank"
              >
                <BillHistoryDownloadIcon
                  path={mdiCloudDownloadOutline}
                  size={1}
                />
              </CommonLink>
            ) : (
              <div>
                <Tooltip
                  showOn="hover"
                  positions={['left']}
                  fullWidth
                  content={<span>{original.status}</span>}
                >
                  <BillHistoryErrorIcon path={mdiAlert} size={1} />
                </Tooltip>
              </div>
            )}
          </BillHistoryStatusCell>
        )}
      </BillHistoryMobileRow>
      <BillHistoryMobileRow>
        <BillHistoryPaymentCell $failed={!isChargeSuccess(original.status)}>
          {original.brand && (
            <PaymentSystem
              src={makePublicUrl(`images/paymentLogos/${original.brand}.svg`)}
              alt={original.brand}
            />
          )}
          <b>**** {original.last4}</b>
        </BillHistoryPaymentCell>
        <BillHistoryAmountCell $failed={!isChargeSuccess(original.status)}>
          {original.amount}
        </BillHistoryAmountCell>
      </BillHistoryMobileRow>
      <BillHistoryMobileRow>
        <BillHistoryHomeNameCell $failed={!isChargeSuccess(original.status)}>
          {original.homeName !== '-' ? (
            <BillHistoryHomeNameLink to={homeStatusPath}>
              {original.homeName}
            </BillHistoryHomeNameLink>
          ) : (
            '-'
          )}
        </BillHistoryHomeNameCell>
      </BillHistoryMobileRow>
      <BillHistoryMobileRow>
        <BillHistoryDescriptionCell $failed={!isChargeSuccess(original.status)}>
          <FormattedMessage {...FormatDescription(original)} />
          <br /> <IntlFormattedDate date={original.startAt * 1000} />-
          <IntlFormattedDate date={original.endAt * 1000} />
        </BillHistoryDescriptionCell>
      </BillHistoryMobileRow>
    </BillHistoryMobileWhiteBlock>
  )
}

export default BillingHistoryMobile
